import React from "react";
import { ReactNode } from "react";
import ObservableState from "../utils/ObservableState";
import * as ui from "../native";
import ObservableComponent from "./ObservableComponent";
import BaseUIProps, { copyBaseUIProps } from "../native/ui/BaseUIProps";
import ObjectObservable from "../utils/ObjectObservable";
import PopupTargetController from "./PopupTargetController";
import ResultStatus from "../classes/ResultStatus";
import SuccessMessage from "../classes/SuccessMessage";
import Popup from "./Popup";
import EventBus from "../utils/EventBus";
import LabelWithDoubleInputField from "./LabelWithDoubleInputField";
import MaterialIcons from "../icons/MaterialIcons";
import IconButton from "./IconButton";
import InvoiceConfiguration from "../models/InvoiceConfiguration";
import Result from "../classes/Result";
import Button from "./Button";
import ListWrapper from "../utils/ListWrapper";
import CheckServerErrors from "./CheckServerErrors";
import Administrator from "../models/Administrator";
import InvoiceDayView from "./InvoiceDayView";
import TextView from "./TextView";
import CollectionUtils from "../utils/CollectionUtils";
import { StyleThemeData } from "./ThemeWrapper";
import { BuildContext } from "../classes/BuildContext";

type _SaveButtonOnPressed = (d3eState: InvoiceConfiguraitonViewRefs) => void;

type _BasePriceRefOnChanged = (
  text: number,
  d3eState: InvoiceConfiguraitonViewRefs
) => void;

type _ExtraChargePerAdvancedRequestRefOnChanged = (
  text: number,
  d3eState: InvoiceConfiguraitonViewRefs
) => void;

type _DiscountRefOnChanged = (
  text: number,
  d3eState: InvoiceConfiguraitonViewRefs
) => void;

type _LateFeeRefOnChanged = (
  text: number,
  d3eState: InvoiceConfiguraitonViewRefs
) => void;

type _MinimumChargeRefOnChanged = (
  text: number,
  d3eState: InvoiceConfiguraitonViewRefs
) => void;

type _TaxationRefOnChanged = (
  text: number,
  d3eState: InvoiceConfiguraitonViewRefs
) => void;

export interface InvoiceConfiguraitonViewProps extends BaseUIProps {
  key?: string;
  admin: Administrator;
}
/// To store state data for InvoiceConfiguraitonView
class InvoiceConfiguraitonViewRefs {
  public saveButton: SaveButtonState = new SaveButtonState();
}

interface SaveButtonWithStateProps extends BaseUIProps {
  key?: string;
  d3eState: InvoiceConfiguraitonViewRefs;
  _onPressedSaveButtonHandler?: _SaveButtonOnPressed;
}

class SaveButtonState extends ObjectObservable {
  private _disable: boolean = false;
  public _hover: boolean = false;
  public get disable(): boolean {
    return this._disable;
  }
  public setDisable(val: boolean) {
    let isValChanged: boolean = this._disable !== val;

    if (!isValChanged) {
      return;
    }

    this._disable = val;

    this.fire("disable", this);
  }
  public get hover(): boolean {
    return this._hover;
  }
  public setHover(val: boolean) {
    let isValChanged: boolean = this._hover !== val;

    if (!isValChanged) {
      return;
    }

    this._hover = val;

    this.fire("hover", this);
  }
}

class _SaveButtonWithState extends ObservableComponent<SaveButtonWithStateProps> {
  saveButtonFocusNode: ui.FocusNode = new ui.FocusNode();
  static contextType = BuildContext;
  context: React.ContextType<typeof BuildContext>;
  public constructor(props: SaveButtonWithStateProps) {
    super(props);

    this.initState();
  }
  public get saveButton(): SaveButtonState {
    return this.props.d3eState.saveButton;
  }
  public get d3eState(): InvoiceConfiguraitonViewRefs {
    return this.props.d3eState;
  }
  public get _onPressedSaveButtonHandler(): _SaveButtonOnPressed {
    return this.props._onPressedSaveButtonHandler;
  }
  public initState() {
    super.initState();

    this.updateObservable("saveButton", null, this.saveButton);

    this.initListeners();

    this.enableBuild = true;
  }
  public initListeners(): void {
    this.on(["saveButton", "saveButton.", "saveButton.hover"], this.rebuild);
  }
  public saveButtonOnEnter(event): void {
    return this.saveButton.setHover(true);
  }
  public saveButtonOnExit(event): void {
    return this.saveButton.setHover(false);
  }
  public dispose(): void {
    this.saveButton.setHover(false);

    super.dispose();
  }
  public render(): ReactNode {
    let cStyle = this.context.theme;

    return ui.Container({
      width: 120,
      child: Button({
        padding: this.saveButton.hover
          ? cStyle.tButtonPrimaryPaddingOnHover
          : cStyle.tButtonPrimaryPaddingOn,
        decoration: this.saveButton.hover
          ? cStyle.tButtonPrimaryDecorationOnHover
          : cStyle.tButtonPrimaryDecorationOn,
        disable: this.saveButton.disable,
        onPressed: () => {
          this._onPressedSaveButtonHandler(this.d3eState);
        },
        onFocusChange: (val) => {},
        child: TextView({ data: "Save" }),
        onEnter: (event) => {
          this.saveButtonOnEnter(event);
        },
        onExit: (event) => {
          this.saveButtonOnExit(event);
        },
      }),
      className: "xcde hc",
    });
  }
}
function SaveButtonWithState(props: SaveButtonWithStateProps) {
  return React.createElement(_SaveButtonWithState, props);
}

class _InvoiceConfiguraitonViewState extends ObservableComponent<InvoiceConfiguraitonViewProps> {
  static defaultProps = { admin: null };
  d3eState: InvoiceConfiguraitonViewRefs = new InvoiceConfiguraitonViewRefs();
  errors: Array<string> = ListWrapper.widget(this, "errors");
  configuration: InvoiceConfiguration = null;
  invoceDayViewPopup: Popup;
  static contextType = BuildContext;
  context: React.ContextType<typeof BuildContext>;
  public editButtonPopupTargetController: PopupTargetController =
    new PopupTargetController();
  public constructor(props: InvoiceConfiguraitonViewProps) {
    super(props);

    this.initState();
  }
  public get admin(): Administrator {
    return this.props.admin;
  }
  public initState() {
    super.initState();

    this.initListeners();

    this.enableBuild = true;

    this.onInit();
  }
  public initListeners(): void {
    this.updateSyncProperty("admin", this.props.admin);

    this.on(
      [
        "configuration",
        "configuration.basePrice",
        "configuration.day",
        "configuration.discount",
        "configuration.lateFee",
        "configuration.minimumPrice",
        "configuration.pricePerAdvancedRequest",
        "configuration.taxation",
        "errors",
      ],
      this.rebuild
    );
  }
  public componentDidUpdate(prevProps: InvoiceConfiguraitonViewProps): void {
    super.componentDidUpdate(prevProps);

    if (prevProps.admin !== this.props.admin) {
      this.updateObservable("admin", prevProps.admin, this.props.admin);

      this.fire("admin", this);
    }
  }
  public setErrors(val: Array<string>): void {
    let isValChanged: boolean = CollectionUtils.isNotEquals(this.errors, val);

    if (!isValChanged) {
      return;
    }

    this.errors.clear();

    this.errors.addAll(val);

    this.fire("errors", this);
  }
  public addToErrors(val: string, index: number = -1): void {
    if (index === -1) {
      if (!this.errors.contains(val)) this.errors.add(val);
    } else {
      this.errors.remove(this.errors.elementAt(index));

      this.errors.add(val);
    }

    this.fire("errors", this, val, true);
  }
  public removeFromErrors(val: string): void {
    this.errors.remove(val);

    this.fire("errors", this, val, false);
  }
  public setConfiguration(val: InvoiceConfiguration): void {
    let isValChanged: boolean = this.configuration !== val;

    if (!isValChanged) {
      return;
    }

    this.updateObservable("configuration", this.configuration, val);

    this.configuration = val;

    this.fire("configuration", this);
  }
  public render(): ReactNode {
    let cStyle = this.context.theme;

    return ui.Column({
      crossAxisAlignment: ui.CrossAxisAlignment.start,
      children: [
        ui.Row({
          mainAxisAlignment: ui.MainAxisAlignment.start,
          children: [
            TextView({
              data: "Every Month Invoice Day",
              style: new ui.TextStyle({ fontWeight: ui.FontWeight.bold }),
              className: "x92e",
              key: "0",
            }),
            TextView({ data: ":", className: "x9e7", key: "1" }),
            TextView({
              data: this.configuration.day.toString(),
              style: new ui.TextStyle({ fontSize: 15 }),
              className: "x69b",
              key: "2",
            }),
            ui.Container({
              margin: ui.EdgeInsets.symmetric({
                horizontal: 5.0,
                vertical: 0.0,
                transitions: new Map(),
              }),
              child: IconButton({
                icon: MaterialIcons.edit,
                color: cStyle.c12,
                size: 20,
                hoverColor: cStyle.c19,
                onPressed: () => {
                  this.onSuccessMessageHandler(this.d3eState);
                },
                d3eRef: this.editButtonPopupTargetController.handleRef,
              }),
              key: "3",
              className: "x970",
            }),
          ],
          className: "x9a1 hc h",
          key: "0",
        }),
        ui.Row({
          mainAxisAlignment: ui.MainAxisAlignment.start,
          children: [
            ui.Container({
              margin: ui.EdgeInsets.fromLTRB(5.0, 0.0, 0.0, 0.0, new Map()),
              child: LabelWithDoubleInputField({
                name: "Base Price",
                value: this.configuration.basePrice,
                width: 400,
                onChanged: (text) => {
                  this.basePriceRefonChanged(text, this.d3eState);
                },
              }),
              key: "0",
              className: "x008",
            }),
            ui.Container({
              margin: ui.EdgeInsets.fromLTRB(5.0, 0.0, 0.0, 0.0, new Map()),
              child: LabelWithDoubleInputField({
                name: "Amount Per Advanced Request",
                value: this.configuration.pricePerAdvancedRequest,
                width: 400,
                onChanged: (text) => {
                  this.extraChargePerAdvancedRequestRefonChanged(
                    text,
                    this.d3eState
                  );
                },
              }),
              key: "1",
              className: "x56",
            }),
          ],
          className: "x74 hc h",
          key: "1",
        }),
        ui.Row({
          mainAxisAlignment: ui.MainAxisAlignment.start,
          children: [
            ui.Container({
              margin: ui.EdgeInsets.fromLTRB(5.0, 0.0, 0.0, 0.0, new Map()),
              child: LabelWithDoubleInputField({
                name: "Discount",
                value: this.configuration.discount,
                width: 400,
                onChanged: (text) => {
                  this.discountRefonChanged(text, this.d3eState);
                },
              }),
              key: "0",
              className: "x596",
            }),
            ui.Container({
              margin: ui.EdgeInsets.fromLTRB(5.0, 0.0, 0.0, 0.0, new Map()),
              child: LabelWithDoubleInputField({
                name: "Late Fee",
                value: this.configuration.lateFee,
                width: 400,
                onChanged: (text) => {
                  this.lateFeeRefonChanged(text, this.d3eState);
                },
              }),
              key: "1",
              className: "xbab",
            }),
          ],
          className: "x02a hc h",
          key: "2",
        }),
        ui.Row({
          mainAxisAlignment: ui.MainAxisAlignment.start,
          children: [
            ui.Container({
              margin: ui.EdgeInsets.fromLTRB(5.0, 0.0, 0.0, 0.0, new Map()),
              child: LabelWithDoubleInputField({
                name: "Minimum Charge",
                value: this.configuration.minimumPrice,
                width: 400,
                onChanged: (text) => {
                  this.minimumChargeRefonChanged(text, this.d3eState);
                },
              }),
              key: "0",
              className: "x9fb",
            }),
            ui.Container({
              margin: ui.EdgeInsets.fromLTRB(5.0, 0.0, 0.0, 0.0, new Map()),
              child: LabelWithDoubleInputField({
                name: "Tax",
                value: this.configuration.taxation,
                width: 400,
                onChanged: (text) => {
                  this.taxationRefonChanged(text, this.d3eState);
                },
              }),
              key: "1",
              className: "x0a4",
            }),
          ],
          className: "x5e4 hc h",
          key: "3",
        }),
        ui.Row({
          mainAxisAlignment: ui.MainAxisAlignment.start,
          children: [
            SaveButtonWithState({
              d3eState: this.d3eState,
              _onPressedSaveButtonHandler: this.onPressedSaveButtonHandler,
              key: "0",
            }),
          ],
          className: "x325 hc h",
          key: "4",
        }),
        this.errors.isNotEmpty
          ? CheckServerErrors({ errors: this.errors, className: "hc" })
          : [],
      ],
      className: ui.join(
        this.props.className,
        "InvoiceConfiguraitonView x9ee hc h"
      ),
      ...copyBaseUIProps(this.props),
    });
  }
  public onDispose = (): void => {
    this.configuration.restore();
  };
  public onInit = (): void => {
    if (this.admin.invoiceConfiguration === null) {
      this.admin.setInvoiceConfiguration(
        new InvoiceConfiguration({ administrator: this.admin })
      );

      this.setConfiguration(this.admin.invoiceConfiguration);
    } else {
      this.setConfiguration(this.admin.invoiceConfiguration);
    }
  };
  public onPressedSaveButtonHandler = async (
    d3eState: InvoiceConfiguraitonViewRefs
  ): Promise<void> => {
    let res: Result<InvoiceConfiguration> = await this.configuration.save();

    if (res.status === ResultStatus.Success) {
      EventBus.get().fire(
        new SuccessMessage({ message: "Saved Successfully" })
      );
    } else {
      this.setErrors(res.errors);
    }
  };
  public onSuccessMessageHandler = (
    d3eState: InvoiceConfiguraitonViewRefs
  ): void => {
    this.showInvoceDayView({ autoClose: true });
  };
  public invoceDayViewHandler = (day: number): void => {
    this.configuration.setDay(day);

    this.hideInvoceDayView();
  };
  public dispose(): void {
    this.invoceDayViewPopup?.dispose();

    super.dispose();

    this.onDispose();
  }
  public showInvoceDayView(
    d3eParams?: Partial<{
      autoClose: boolean;
      model: boolean;
      float: boolean;
      takeFocus: boolean;
    }>
  ): void {
    let autoClose = d3eParams?.autoClose;

    let model = d3eParams?.model;

    let float = d3eParams?.float;

    let takeFocus = d3eParams?.takeFocus;

    this.invoceDayViewPopup?.dispose();

    /*
TODO ThemeWrapper.of(this.context);
*/

    let cStyle = StyleThemeData.current;

    let target: ui.Rect = this.editButtonPopupTargetController.getTarget(
      this.context
    );

    this.invoceDayViewPopup = new Popup({
      autoClose: autoClose,
      model: model,
      float: float,
      takeFocus: takeFocus,
      position: ui.PopUpPosition.Right,
      child: ui.Container({
        decoration: new ui.BoxDecoration({
          border: ui.Border.all({ color: cStyle.c23, width: 1.0 }),
          boxShadow: [
            new ui.BoxShadow({
              color: cStyle.c4,
              blurRadius: 3,
              spreadRadius: 1,
            }),
          ],
        }),
        height: 200,
        width: 300,
        child: InvoiceDayView({
          selectedDay: this.configuration.day,
          onDaySelected: (day) => {
            this.invoceDayViewHandler(day);
          },
        }),
        className: "xbec hc vc",
      }),
      target: target,
    });

    this.invoceDayViewPopup.showPopup(this.context);
  }
  public hideInvoceDayView(): void {
    this.invoceDayViewPopup?.dispose();
  }
  public basePriceRefonChanged = (
    val: number,
    d3eState: InvoiceConfiguraitonViewRefs
  ): void => {
    this.configuration.setBasePrice(val);
  };
  public extraChargePerAdvancedRequestRefonChanged = (
    val: number,
    d3eState: InvoiceConfiguraitonViewRefs
  ): void => {
    this.configuration.setPricePerAdvancedRequest(val);
  };
  public discountRefonChanged = (
    val: number,
    d3eState: InvoiceConfiguraitonViewRefs
  ): void => {
    this.configuration.setDiscount(val);
  };
  public lateFeeRefonChanged = (
    val: number,
    d3eState: InvoiceConfiguraitonViewRefs
  ): void => {
    this.configuration.setLateFee(val);
  };
  public minimumChargeRefonChanged = (
    val: number,
    d3eState: InvoiceConfiguraitonViewRefs
  ): void => {
    this.configuration.setMinimumPrice(val);
  };
  public taxationRefonChanged = (
    val: number,
    d3eState: InvoiceConfiguraitonViewRefs
  ): void => {
    this.configuration.setTaxation(val);
  };
  public get saveButton() {
    return this.d3eState.saveButton;
  }
}
export default function InvoiceConfiguraitonView(
  props: InvoiceConfiguraitonViewProps
) {
  return React.createElement(_InvoiceConfiguraitonViewState, {
    ..._InvoiceConfiguraitonViewState.defaultProps,
    ...props,
  });
}
